import { useState } from 'react'
import {
  Globe,
  Terminal,
  MousePointer,
  Navigation,
  Info,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  AlertTriangle,
  FileText,
} from 'lucide-react'
import type { Breadcrumb } from '../types'

interface BreadcrumbsTimelineProps {
  breadcrumbs: Breadcrumb[]
  errorTimestamp: string
}

const BREADCRUMB_ICONS: Record<string, typeof Globe> = {
  http: Globe,
  console: Terminal,
  click: MousePointer,
  navigation: Navigation,
  custom: Info,
}

const CATEGORY_COLORS: Record<string, string> = {
  fetch: 'bg-blue-100 text-blue-700',
  xhr: 'bg-blue-100 text-blue-700',
  log: 'bg-slate-100 text-slate-600',
  info: 'bg-slate-100 text-slate-600',
  debug: 'bg-slate-100 text-slate-500',
  warn: 'bg-amber-100 text-amber-700',
  error: 'bg-red-100 text-red-700',
  'ui.click': 'bg-purple-100 text-purple-700',
  navigation: 'bg-emerald-100 text-emerald-700',
}

const TYPE_COLORS: Record<string, string> = {
  http: 'border-blue-200',
  console: 'border-slate-200',
  click: 'border-purple-200',
  navigation: 'border-emerald-200',
  custom: 'border-slate-200',
}

export default function BreadcrumbsTimeline({
  breadcrumbs,
  errorTimestamp,
}: BreadcrumbsTimelineProps) {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set())

  const toggleItem = (index: number) => {
    setExpandedItems(prev => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  const formatRelativeTime = (timestamp: string, errorTime: string) => {
    const breadcrumbDate = new Date(timestamp)
    const errorDate = new Date(errorTime)
    const diffMs = errorDate.getTime() - breadcrumbDate.getTime()

    if (diffMs < 0) return 'after error'

    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffMs / 60000)

    if (diffSeconds < 1) return 'at error'
    if (diffSeconds < 60) return `-${diffSeconds}s`
    if (diffMinutes < 60) return `-${diffMinutes}m ${diffSeconds % 60}s`
    return `-${Math.floor(diffMinutes / 60)}h ${diffMinutes % 60}m`
  }

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString()
  }

  const getCategoryIcon = (breadcrumb: Breadcrumb) => {
    if (breadcrumb.type === 'console') {
      if (breadcrumb.category === 'error') return AlertCircle
      if (breadcrumb.category === 'warn') return AlertTriangle
      return Terminal
    }
    return BREADCRUMB_ICONS[breadcrumb.type] || FileText
  }

  if (!breadcrumbs || breadcrumbs.length === 0) {
    return null
  }

  return (
    <div className="border-t border-slate-200">
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-100">
        <Navigation className="w-4 h-4 text-slate-500" />
        <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
          Breadcrumbs ({breadcrumbs.length})
        </span>
      </div>

      <div className="p-4 bg-slate-50">
        <div className="space-y-1">
          {breadcrumbs.map((breadcrumb, index) => {
            const Icon = getCategoryIcon(breadcrumb)
            const isExpanded = expandedItems.has(index)
            const hasData = breadcrumb.data && Object.keys(breadcrumb.data).length > 0
            const categoryColor =
              CATEGORY_COLORS[breadcrumb.category || ''] || 'bg-slate-100 text-slate-600'
            const borderColor = TYPE_COLORS[breadcrumb.type] || 'border-slate-200'

            return (
              <div key={index} className={`border-l-2 ${borderColor} pl-3 py-2`}>
                <div
                  className={`flex items-start gap-2 ${hasData ? 'cursor-pointer' : ''}`}
                  onClick={() => hasData && toggleItem(index)}
                >
                  {/* Timeline marker */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Icon className="w-4 h-4 text-slate-400" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Category badge */}
                      {breadcrumb.category && (
                        <span
                          className={`px-1.5 py-0.5 rounded text-xs font-medium ${categoryColor}`}
                        >
                          {breadcrumb.category}
                        </span>
                      )}
                      {/* Message */}
                      <span className="text-sm text-slate-700 truncate">{breadcrumb.message}</span>
                    </div>
                  </div>

                  {/* Timestamp and expand icon */}
                  <div className="flex items-center gap-2 flex-shrink-0 text-xs text-slate-500">
                    <span className="font-mono">
                      {formatRelativeTime(breadcrumb.timestamp, errorTimestamp)}
                    </span>
                    <span className="text-slate-400">{formatTimestamp(breadcrumb.timestamp)}</span>
                    {hasData &&
                      (isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      ))}
                  </div>
                </div>

                {/* Expanded data section */}
                {isExpanded && hasData && (
                  <div className="mt-2 ml-6">
                    <pre className="text-xs bg-white border border-slate-200 rounded p-2 overflow-x-auto text-slate-700 font-mono">
                      {JSON.stringify(breadcrumb.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
